import { useEffect, useState } from "react";
import { api, formatApiError as formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UsersFour, Plus, Trash } from "@phosphor-icons/react";

export default function AdminHosts() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", department: "" });

    const load = async () => setItems((await api.get("/gate-check/hosts")).data);
    useEffect(() => { load(); }, []);

    const add = async () => {
        try {
            await api.post("/gate-check/hosts", form);
            toast.success("Host added");
            setForm({ name: "", email: "", phone: "", department: "" });
            setOpen(false); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const remove = async (id) => { await api.delete(`/gate-check/hosts/${id}`); toast.success("Removed"); load(); };

    return (
        <div>
            <PageHeader
                overline="Admin"
                title="Hosts & Employees"
                subtitle="Directory of internal employees who receive visitor arrival alerts."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="btn-add-host" className="h-11 bg-brand hover:bg-brand/90 text-white">
                                <Plus size={16} weight="bold" className="mr-2" /> Add Host
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-display">Add Host</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label className="overline text-[10px]">Full Name *</Label>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        data-testid="input-host-name" className="h-11 mt-1" />
                                </div>
                                <div>
                                    <Label className="overline text-[10px]">Email *</Label>
                                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        data-testid="input-host-email" className="h-11 mt-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="overline text-[10px]">Phone *</Label>
                                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            data-testid="input-host-phone" className="h-11 mt-1" />
                                    </div>
                                    <div>
                                        <Label className="overline text-[10px]">Department</Label>
                                        <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
                                            data-testid="input-host-dept" className="h-11 mt-1" />
                                    </div>
                                </div>
                                <Button onClick={add} data-testid="btn-save-host"
                                    className="w-full h-11 bg-brand hover:bg-brand/90 text-white">Save Host</Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                }
            />
            <div className="p-6">
                <Card className="tech-card overflow-hidden">
                    <div className="divide-y divide-border">
                        {items.length === 0 && <div className="p-8 text-center text-muted-foreground">No hosts.</div>}
                        {items.map((h) => (
                            <div key={h.id} className="px-5 py-4 flex items-center gap-4" data-testid={`row-host-${h.id}`}>
                                <div className="h-10 w-10 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold">
                                    {h.name[0]?.toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold">{h.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {h.email} · {h.phone} · {h.department || "—"}
                                    </div>
                                </div>
                                <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => remove(h.id)}
                                    data-testid={`btn-remove-host-${h.id}`}>
                                    <Trash size={16} />
                                </Button>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}
